"use client"

import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Download, FileText, Send, Filter } from "lucide-react"
import { useCases } from "@/lib/mock-data"
import { ClassificationBadge, FeasibilityBadge, PriorityBadge, StatusBadge } from "@/components/status-badges"

function HorizontalBar({ label, count, max, color }: { label: string; count: number; max: number; color: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground w-36 shrink-0 truncate">{label}</span>
      <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
        <div
          className={`h-full rounded transition-all ${color}`}
          style={{ width: `${max > 0 ? (count / max) * 100 : 0}%` }}
        />
      </div>
      <span className="text-xs font-semibold w-6 text-right shrink-0">{count}</span>
    </div>
  )
}

const reports = [
  {
    id: "by-dept",
    title: "AI Use Cases by Department",
    description: "Distribution of use cases across business departments.",
  },
  {
    id: "high-priority",
    title: "High Priority Use Cases",
    description: "Use cases with P1 priority band (score 80–100).",
  },
  {
    id: "l1-data",
    title: "L1 Data Use Cases",
    description: "Use cases involving confidential (L1) data classification.",
  },
  {
    id: "pending-feasibility",
    title: "Use Cases Pending Feasibility",
    description: "Use cases awaiting feasibility review from any team.",
  },
  {
    id: "blocked-infra",
    title: "Use Cases Blocked by Infra",
    description: "Use cases where Infrastructure review has blockers.",
  },
  {
    id: "blocked-network",
    title: "Use Cases Blocked by Network",
    description: "Use cases where Network review has blockers.",
  },
  {
    id: "approved",
    title: "Use Cases Approved for Discovery",
    description: "Use cases approved and ready for proof of concept.",
  },
  {
    id: "parked-rejected",
    title: "Parked or Rejected Use Cases",
    description: "Use cases that have been parked or rejected.",
  },
]

export function ReportsContent() {
  // Compute report data
  const byDept = Object.entries(
    useCases.reduce<Record<string, number>>((acc, uc) => {
      acc[uc.department] = (acc[uc.department] || 0) + 1
      return acc
    }, {})
  ).sort((a, b) => b[1] - a[1])
  const maxDept = Math.max(...byDept.map(([, c]) => c), 1)

  const highPriority = useCases.filter((uc) => uc.priorityBand === "P1")
  const l1Data = useCases.filter((uc) => uc.dataClassification === "L1")
  const pendingFeasibility = useCases.filter((uc) => uc.status === "Feasibility Pending")
  const approved = useCases.filter((uc) => uc.status === "Approved for Discovery")
  const parkedRejected = useCases.filter((uc) => uc.status === "Parked" || uc.status === "Rejected")

  return (
    <div className="p-6 flex flex-col gap-6">
      {/* Export Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => toast.success("CSV export initiated.")}
        >
          <Download className="size-4" />
          Export CSV
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => toast.info("PDF export coming soon.")}
        >
          <FileText className="size-4" />
          Export PDF
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => toast.success("Report sent to Steering Committee.")}
        >
          <Send className="size-4" />
          Send to Steering Committee
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-muted-foreground">Report Date: {new Date().toLocaleDateString("en-GB")}</span>
        </div>
      </div>

      {/* Report cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* By Department */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-sm">AI Use Cases by Department</CardTitle>
                <CardDescription className="text-xs">Distribution across business departments</CardDescription>
              </div>
              <Badge variant="secondary" className="text-xs shrink-0">{useCases.length} total</Badge>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-2.5">
            {byDept.map(([dept, count]) => (
              <HorizontalBar key={dept} label={dept} count={count} max={maxDept} color="bg-blue-500" />
            ))}
          </CardContent>
        </Card>

        {/* By Feasibility + Classification */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Feasibility & Data Classification</CardTitle>
            <CardDescription className="text-xs">Breakdown by feasibility flag and data level</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-muted-foreground">Feasibility</span>
              {[
                { label: "Green — Feasible", count: useCases.filter((u) => u.feasibilityFlag === "Green").length, color: "bg-emerald-500" },
                { label: "Amber — Conditions", count: useCases.filter((u) => u.feasibilityFlag === "Amber").length, color: "bg-amber-500" },
                { label: "Red — Not Feasible", count: useCases.filter((u) => u.feasibilityFlag === "Red").length, color: "bg-red-500" },
              ].map((item) => (
                <HorizontalBar key={item.label} {...item} max={useCases.length} />
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-muted-foreground">Data Classification</span>
              {[
                { label: "Public", count: useCases.filter((u) => u.dataClassification === "Public").length, color: "bg-emerald-400" },
                { label: "L0 — Internal", count: useCases.filter((u) => u.dataClassification === "L0").length, color: "bg-blue-500" },
                { label: "L1 — Confidential", count: useCases.filter((u) => u.dataClassification === "L1").length, color: "bg-orange-500" },
              ].map((item) => (
                <HorizontalBar key={item.label} {...item} max={useCases.length} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* High Priority */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-sm">High Priority Use Cases</CardTitle>
                <CardDescription className="text-xs">P1 use cases — Score 80+</CardDescription>
              </div>
              <Badge variant="destructive" className="text-xs">{highPriority.length} P1</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {highPriority.length === 0 ? (
              <p className="text-xs text-muted-foreground px-5 pb-4">No P1 use cases found.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-medium text-muted-foreground px-5 py-2">Name</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Score</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {highPriority.map((uc) => (
                    <tr key={uc.id} className="border-b border-border last:border-0">
                      <td className="px-5 py-2.5">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-medium">{uc.name}</span>
                          <span className="text-xs text-muted-foreground">{uc.department}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-base font-bold">{uc.priorityScore}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge value={uc.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* L1 Data */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-sm">L1 Data Use Cases</CardTitle>
                <CardDescription className="text-xs">Use cases involving confidential data</CardDescription>
              </div>
              <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-800">{l1Data.length} L1</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {l1Data.length === 0 ? (
              <p className="text-xs text-muted-foreground px-5 pb-4">No L1 data use cases.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-medium text-muted-foreground px-5 py-2">Name</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Feasibility</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {l1Data.map((uc) => (
                    <tr key={uc.id} className="border-b border-border last:border-0">
                      <td className="px-5 py-2.5">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-medium">{uc.name}</span>
                          <span className="text-xs text-muted-foreground">{uc.department}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <FeasibilityBadge value={uc.feasibilityFlag} />
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge value={uc.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Pending Feasibility */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-sm">Pending Feasibility</CardTitle>
                <CardDescription className="text-xs">Awaiting technical review</CardDescription>
              </div>
              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">{pendingFeasibility.length}</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {pendingFeasibility.length === 0 ? (
              <p className="text-xs text-muted-foreground px-5 pb-4">No use cases pending feasibility.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-medium text-muted-foreground px-5 py-2">Name</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Dept</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Classification</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingFeasibility.map((uc) => (
                    <tr key={uc.id} className="border-b border-border last:border-0">
                      <td className="px-5 py-2.5"><span className="text-xs font-medium">{uc.name}</span></td>
                      <td className="px-4 py-2.5"><span className="text-xs text-muted-foreground">{uc.department}</span></td>
                      <td className="px-4 py-2.5"><ClassificationBadge value={uc.dataClassification} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Approved */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-sm">Approved for Discovery</CardTitle>
                <CardDescription className="text-xs">Ready for proof of concept</CardDescription>
              </div>
              <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-800">{approved.length}</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {approved.length === 0 ? (
              <p className="text-xs text-muted-foreground px-5 pb-4">No approved use cases yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-medium text-muted-foreground px-5 py-2">Name</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Priority</th>
                    <th className="text-left text-xs font-medium text-muted-foreground px-4 py-2">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {approved.map((uc) => (
                    <tr key={uc.id} className="border-b border-border last:border-0">
                      <td className="px-5 py-2.5">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-medium">{uc.name}</span>
                          <span className="text-xs text-muted-foreground">{uc.department}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5"><PriorityBadge value={uc.priorityBand} /></td>
                      <td className="px-4 py-2.5"><span className="text-base font-bold">{uc.priorityScore}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
